import { useState, useEffect, useCallback } from "react";
import { GetMyProfileAction, isAuthenticated } from "../store/action";

/**
 * The signed-in account's own details: name, email, phone, city of residence.
 *
 * Shared by the Settings panel that edits them and the surfaces that consume
 * them — the booking form pre-fill and the business search's default city — so
 * "what do we know about this person" has one answer.
 *
 * A failed load is not an error state worth showing anywhere. Every consumer
 * treats the profile as a convenience: the booking form falls back to empty
 * fields the customer can type into, and the search falls back to every city.
 * Both are the pre-existing behaviour, so a dead profile endpoint degrades to
 * the old experience rather than blocking a booking. Settings is the one caller
 * that surfaces `error`, because there the profile *is* the content.
 *
 * **It does not fetch without a session, and that guard is load-bearing.** The
 * public booking page mounts this so a signed-in customer gets their details
 * pre-filled there too, and it is reached by anonymous visitors constantly. A
 * request sent without a token comes back 401, and the response interceptor
 * reads a 401-with-no-token as a dead session and redirects to /login — so
 * asking for the profile anyway would bounce every anonymous visitor off the
 * booking page they were trying to use.
 */
export const useMyProfile = () => {
  const [profile, setProfile] = useState(null);
  // Nothing to wait for when there is no session, so callers that gate on
  // `loading` render immediately rather than flashing a spinner at a visitor
  // who will never have a profile.
  const [loading, setLoading] = useState(() => isAuthenticated());
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated()) {
      setProfile(null);
      setError(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await GetMyProfileAction();
      setProfile(data ?? null);
      setError(false);
    } catch {
      setProfile(null);
      // A flag rather than a message: nothing here closes over `t`, so a
      // language switch cannot re-trigger the fetch.
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { profile, loading, error, reload: load, setProfile };
};

/**
 * The profile as the booking form wants it, or null when there is nothing worth
 * pre-filling.
 *
 * Name and email are the two fields the form marks required, so a profile
 * missing either is not usable as a pre-fill — it would present a half-filled
 * "your details are on file" summary that still refuses to submit. The phone is
 * optional here and stays editable in the form.
 */
export const toBookingPrefill = (profile) => {
  if (!profile?.email) return null;

  const fullName = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (!fullName) return null;

  return { fullName, email: profile.email, phone: profile.phone || "" };
};
